/**
 * Recent Trades Component
 * Displays recent closed trades with wash sale indicators
 */

import { useRecentTrades } from '../api/client';

export function RecentTrades() {
  const { data, isLoading, error } = useRecentTrades(20);

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading recent trades...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading trades: {error.message}</div>
      </div>
    );
  }

  const trades = data?.recentTrades || [];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Recent Trades</h2>
        <span className="card-badge badge-success">{trades.length} Closed</span>
      </div>

      {trades.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💰</div>
          <p>No recent trades</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>Quantity</th>
              <th>P&L ($)</th>
              <th>P&L (%)</th>
              <th>Duration</th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((trade) => {
              const hours = Math.floor(trade.holdDurationMinutes / 60);
              const minutes = trade.holdDurationMinutes % 60;
              const duration =
                hours > 24
                  ? `${Math.floor(hours / 24)}d ${hours % 24}h`
                  : hours > 0
                  ? `${hours}h ${minutes}m`
                  : `${minutes}m`;

              return (
                <tr key={trade.id}>
                  <td>
                    <strong>{trade.symbol}</strong>
                  </td>
                  <td>
                    <div style={{ fontSize: '0.875rem' }}>
                      ${trade.entryPrice.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {new Date(trade.entryTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '0.875rem' }}>
                      ${trade.exitPrice.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {new Date(trade.exitTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  </td>
                  <td>{trade.quantity}</td>
                  <td className={trade.pnl >= 0 ? 'positive' : 'negative'}>
                    {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                  </td>
                  <td className={trade.pnlPct >= 0 ? 'positive' : 'negative'}>
                    {trade.pnlPct >= 0 ? '+' : ''}
                    {trade.pnlPct.toFixed(2)}%
                  </td>
                  <td style={{ fontSize: '0.875rem' }}>{duration}</td>
                  <td>
                    {trade.washSale && (
                      <span
                        className="card-badge badge-warning"
                        title="Wash Sale Detected - Loss disallowed for tax purposes"
                      >
                        WS
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {trades.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Total P&L: $
          {trades
            .reduce((sum, t) => sum + t.pnl, 0)
            .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
    </div>
  );
}
